import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Lock, AlertCircle } from 'lucide-react';
import { setInitialPasswordSchema } from '@barrikadave/shared';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

export default function ForceChangePasswordPage() {
  const { user, logout, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If the flag is already false, no reason to be on this page.
  if (!user?.mustChangePassword) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const result = setInitialPasswordSchema.safeParse({ newPassword });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? 'Fjalëkalim i pavlefshëm');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Fjalëkalimet nuk përputhen');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.setInitialPassword(newPassword);
      clearMustChangePassword();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Vendosja e fjalëkalimit dështoi',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-barrikadave-50 border-2 border-barrikadave-500 flex items-center justify-center">
            <Lock className="w-7 h-7 text-barrikadave-500" />
          </div>
        </div>

        <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
          Vendos një fjalëkalim të ri
        </h1>
        <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
          {user?.firstName ? `Përshëndetje ${user.firstName}, p` : 'P'}ër arsye
          sigurie, përpara se të përdorni panelin duhet të ndryshoni
          fjalëkalimin që morët me email.
        </p>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 mb-4">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fjalëkalimi i ri
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Së paku 8 karaktere"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-barrikadave-500 focus:border-transparent"
              disabled={submitting}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Konfirmo fjalëkalimin
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Përsërit fjalëkalimin e ri"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-barrikadave-500 focus:border-transparent"
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-barrikadave-500 hover:bg-barrikadave-600 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center"
          >
            {submitting ? <LoadingSpinner size="sm" /> : 'Ruaj fjalëkalimin'}
          </button>

          <button
            type="button"
            onClick={() => logout()}
            disabled={submitting}
            className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
          >
            Dil
          </button>
        </form>
      </div>
    </div>
  );
}
